import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';
import './TimePicker.scss';

const PRESET_TIMES = [
  '08:00', '09:00', '10:00', '12:00',
  '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
];

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

function TimePicker({
  label,
  value = '',
  onChange,
  placeholder = 'HH:MM',
  disabled = false,
  error,
  required = false,
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const hoursListRef = useRef(null);
  const minutesListRef = useRef(null);

  // Parse current value "HH:MM"
  const [currentHour, currentMinute] = (value || '').split(':');

  // Reposition dropdown on resize / scroll
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      const dropdownWidth = 260;
      const dropdownHeight = 280;

      const spaceBelow = window.innerHeight - rect.bottom;
      const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

      let top = showAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4;
      let left = rect.left;

      if (left + dropdownWidth > window.innerWidth - 12) {
        left = Math.max(12, window.innerWidth - dropdownWidth - 12);
      }
      if (left < 12) left = 12;

      setDropdownStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 9999,
        width: `${dropdownWidth}px`,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      const inInput = inputRef.current && inputRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inInput && !inDropdown) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Scroll active hour/minute into view when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (hoursListRef.current) {
          const selectedH = hoursListRef.current.querySelector('.timepicker__item--selected');
          if (selectedH) selectedH.scrollIntoView({ block: 'center' });
        }
        if (minutesListRef.current) {
          const selectedM = minutesListRef.current.querySelector('.timepicker__item--selected');
          if (selectedM) selectedM.scrollIntoView({ block: 'center' });
        }
      }, 50);
    }
  }, [isOpen]);

  const emitChange = (newVal) => {
    const eventObj = {
      target: { value: newVal },
      value: newVal,
    };
    onChange?.(newVal, eventObj);
  };

  const handleHourSelect = (h) => {
    const m = currentMinute || '00';
    emitChange(`${h}:${m}`);
  };

  const handleMinuteSelect = (m) => {
    const h = currentHour || '12';
    emitChange(`${h}:${m}`);
  };

  const handlePresetSelect = (timeStr) => {
    emitChange(timeStr);
    setIsOpen(false);
  };

  const handleClear = () => {
    emitChange('');
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    emitChange(e.target.value);
  };

  const dropdownJSX = isOpen ? (
    <div
      className="timepicker__dropdown timepicker__dropdown--open"
      style={dropdownStyle}
      ref={dropdownRef}
    >
      {/* Preset pills */}
      <div className="timepicker__presets">
        {PRESET_TIMES.map((time) => (
          <button
            key={time}
            type="button"
            className={`timepicker__preset-btn ${value === time ? 'timepicker__preset-btn--selected' : ''}`}
            onClick={() => handlePresetSelect(time)}
          >
            {time}
          </button>
        ))}
      </div>

      {/* Hour & Minute Pickers */}
      <div className="timepicker__columns">
        <div className="timepicker__column">
          <span className="timepicker__column-label">Hodina</span>
          <div className="timepicker__list" ref={hoursListRef}>
            {HOURS.map((h) => (
              <button
                key={h}
                type="button"
                className={`timepicker__item ${currentHour === h ? 'timepicker__item--selected' : ''}`}
                onClick={() => handleHourSelect(h)}
              >
                {h}
              </button>
            ))}
          </div>
        </div>

        <div className="timepicker__column">
          <span className="timepicker__column-label">Minúta</span>
          <div className="timepicker__list" ref={minutesListRef}>
            {MINUTES.map((m) => (
              <button
                key={m}
                type="button"
                className={`timepicker__item ${currentMinute === m ? 'timepicker__item--selected' : ''}`}
                onClick={() => handleMinuteSelect(m)}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="timepicker__footer">
        <button type="button" className="timepicker__clear-btn" onClick={handleClear}>
          Vymazať
        </button>
        <button type="button" className="timepicker__confirm-btn" onClick={() => setIsOpen(false)}>
          Hotovo
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className={`timepicker ${className}`} ref={inputRef}>
      {label && <label className="timepicker__label">{label}</label>}

      <div className="timepicker__input-wrapper">
        <input
          type="text"
          className={`timepicker__input ${isOpen ? 'timepicker__input--open' : ''} ${
            disabled ? 'timepicker__input--disabled' : ''
          } ${error ? 'timepicker__input--error' : ''}`}
          value={value || ''}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          onChange={handleInputChange}
          onClick={() => !disabled && setIsOpen(true)}
        />
        <Clock className="timepicker__icon" size={18} />
      </div>

      {dropdownJSX && createPortal(dropdownJSX, document.body)}

      {error && <span className="timepicker__error">{error}</span>}
    </div>
  );
}

export default TimePicker;
